/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.SYSTEM.AUDIT_LOG — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/AuditLogPage.tsx. The desktop screen
 * is a filterable, paginated forensic grid over csm_audit_log with an expandable
 * field-level diff; the mobile presentation is a DISTINCT, scannable audit-activity
 * feed over the SAME table:
 *   - reads the recent csm_audit_log entries directly via apiDb (the same columns
 *     the desktop selects), resolving each actor (user_id) to a display name
 *     (§17.B4 — never a raw id reaches render),
 *   - a facet-filtered card list + bottom-sheet detail that shows the full context
 *     (action, when, actor, IP, override reason) and the field-level before/after
 *     delta for the tapped entry.
 * It is READ-ONLY (the audit log is never mutated), so it imports no write path.
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Plus, Pencil, Trash2, ShieldAlert, History } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow,
  MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// How many most-recent audit entries to pull — a bounded, mobile-friendly feed
// (RLS still scopes it to admins, mirroring the desktop csm_al_admin_select policy).
const MAX_ROWS = 200;

interface MobileAuditRow {
  audit_log_id: number;
  log_timestamp: string;
  action_type: string;
  entity_name: string;
  record_id: string | null;
  user_id: number | null;
  ip_address: string | null;
  override_reason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

type ActionFacet = 'all' | 'INSERT' | 'UPDATE' | 'DELETE' | 'SECURITY_OVERRIDE';

interface ActionMeta { labelKey: string; label: string; pill: MobilePillTone; lead: MobileLeadTone }

// CHECK-enum-style action labels: localized via t(), never the bare stored literal
// (§17.B11). Each carries the pill + lead-icon-tint the card/detail uses.
const ACTION_META: Record<string, ActionMeta> = {
  INSERT:            { labelKey: 'mobile.audit.action.INSERT',            label: 'Insert',            pill: 'ok',   lead: 'green' },
  UPDATE:            { labelKey: 'mobile.audit.action.UPDATE',            label: 'Update',            pill: 'info', lead: 'blue' },
  DELETE:            { labelKey: 'mobile.audit.action.DELETE',            label: 'Delete',            pill: 'no',   lead: 'red' },
  SECURITY_OVERRIDE: { labelKey: 'mobile.audit.action.SECURITY_OVERRIDE', label: 'Security override', pill: 'wait', lead: 'amber' },
};

const FACET_KEYS: ActionFacet[] = ['all', 'INSERT', 'UPDATE', 'DELETE', 'SECURITY_OVERRIDE'];

function ActionIcon({ action }: { action: string }) {
  if (action === 'INSERT') return <Plus />;
  if (action === 'UPDATE') return <Pencil />;
  if (action === 'DELETE') return <Trash2 />;
  if (action === 'SECURITY_OVERRIDE') return <ShieldAlert />;
  return <History />;
}

function changedKeys(row: MobileAuditRow): string[] {
  return Array.from(new Set([...Object.keys(row.before ?? {}), ...Object.keys(row.after ?? {})]));
}

function valueText(value: unknown): string {
  if (value === undefined) return '—';
  return JSON.stringify(value);
}

export function MobileAuditLogPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const fmtDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileAuditRow[]>([]);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState('');
  const [actionFacet, setActionFacet] = useState<ActionFacet>('all');
  const [selected, setSelected] = useState<MobileAuditRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('csm_audit_log')
        .select('audit_log_id, log_timestamp, action_type, entity_name, record_id, user_id, ip_address, override_reason, old_values, new_values')
        .order('log_timestamp', { ascending: false })
        .limit(MAX_ROWS);
      if (e) throw e;

      type RawRow = {
        audit_log_id: number; log_timestamp: string; action_type: string;
        entity_name: string; record_id: string | null; user_id: number | null;
        ip_address: string | null; override_reason: string | null;
        old_values: Record<string, unknown> | null; new_values: Record<string, unknown> | null;
      };
      const list: MobileAuditRow[] = ((data ?? []) as RawRow[]).map((r) => ({
        audit_log_id: r.audit_log_id,
        log_timestamp: r.log_timestamp,
        action_type: r.action_type,
        entity_name: r.entity_name,
        record_id: r.record_id,
        user_id: r.user_id,
        ip_address: r.ip_address,
        override_reason: r.override_reason,
        before: r.old_values,
        after: r.new_values,
      }));
      setRows(list);

      // Resolve actor ids -> display names (§17.B4 — never a raw id reaches render).
      const ids = Array.from(new Set(list.map((r) => r.user_id).filter((x): x is number => x != null)));
      if (ids.length) {
        const { data: us } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', ids);
        const m: Record<number, string> = {};
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) {
          m[u.user_id] = u.display_name || u.email || String(u.user_id);
        }
        setUserNames(m);
      } else {
        setUserNames({});
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (labels follow it — §8).
  useEffect(() => { void reload(); }, [activeLocale]);

  const actorName = (userId: number | null): string =>
    userId == null ? t('common.none', 'None') : (userNames[userId] ?? String(userId));

  const actionLabel = (action: string): string => {
    const meta = ACTION_META[action];
    return meta ? t(meta.labelKey, meta.label) : action;
  };

  const actionPill = (action: string): MobilePillTone => ACTION_META[action]?.pill ?? 'info';
  const actionLead = (action: string): MobileLeadTone => ACTION_META[action]?.lead ?? 'slate';

  const recordLabel = (id: string | null): string =>
    id == null ? t('mobile.audit.no_record', 'No record') : t('mobile.audit.record', 'Record {{id}}', { id });

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (actionFacet !== 'all' && r.action_type !== actionFacet) return false;
      if (!q) return true;
      return (
        r.entity_name.toLowerCase().includes(q) ||
        (r.record_id ?? '').toLowerCase().includes(q) ||
        actorName(r.user_id).toLowerCase().includes(q)
      );
    });
    // actorName depends on userNames; recompute when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, actionFacet, userNames]);

  const facetLabel = (key: ActionFacet): string =>
    key === 'all' ? t('mobile.audit.all', 'All') : actionLabel(key);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.audit.title', 'Audit Log')}
        description={t('mobile.audit.description', 'Recent forensic record of every create, change and delete.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.audit.search', 'Search entity, record or actor')}
          aria-label={t('mobile.audit.search', 'Search entity, record or actor')}
        />
      </div>

      <MobileFilterChips
        chips={FACET_KEYS.map((key) => ({ value: key, label: facetLabel(key) }))}
        isActive={(v) => actionFacet === v}
        onSelect={(v) => setActionFacet(v as ActionFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.audit.none', 'No audit entries match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.audit_log_id}
              lead={<ActionIcon action={r.action_type} />}
              leadTone={actionLead(r.action_type)}
              title={r.entity_name}
              subtitle={`${recordLabel(r.record_id)} · ${actorName(r.user_id)} · ${fmtDateTime(r.log_timestamp)}`}
              trailing={<MobileStatusPill tone={actionPill(r.action_type)}>{actionLabel(r.action_type)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet — read-only forensic view of one audit entry. */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.audit.detail', 'Audit entry detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ActionIcon action={selected.action_type} /></span>
              <span>
                <div className="m-nm">{selected.entity_name}</div>
                <div className="m-em">{recordLabel(selected.record_id)}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.audit.action_label', 'Action')}
                value={<MobileStatusPill tone={actionPill(selected.action_type)}>{actionLabel(selected.action_type)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.audit.when', 'When')} value={fmtDateTime(selected.log_timestamp)} />
              <MobileDetailRow label={t('mobile.audit.actor', 'By')} value={actorName(selected.user_id)} />
              <MobileDetailRow label={t('mobile.audit.ip', 'IP address')} value={selected.ip_address ?? '—'} />
              {selected.override_reason && (
                <MobileDetailRow
                  label={t('mobile.audit.override_reason', 'Override reason')}
                  value={<span style={{ textAlign: 'left' }}>{selected.override_reason}</span>}
                />
              )}
            </MobileContentCard>

            <MobileContentCard>
              {changedKeys(selected).length === 0 ? (
                <div className="m-empty">{t('mobile.audit.no_delta', 'No field-level changes recorded.')}</div>
              ) : (
                changedKeys(selected).map((k) => (
                  <MobileDetailRow
                    key={k}
                    label={k}
                    value={
                      <span style={{ textAlign: 'left' }}>
                        <span style={{ color: 'var(--color-danger)' }}>{valueText(selected.before?.[k])}</span>
                        {' → '}
                        <span style={{ color: 'var(--color-primary)' }}>{valueText(selected.after?.[k])}</span>
                      </span>
                    }
                  />
                ))
              )}
            </MobileContentCard>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
